// Curated named-client case studies whose deep content lives in Sanity.
// Slugs match the `caseStudy.slug.current` field in Sanity.
import ironmanLogo from "@/assets/logos/ironman-logo.svg";
import uelLogo from "@/assets/logos/uel-logo.svg";
import kohlerLogo from "@/assets/logos/kohler-logo.svg";
import idmcLogo from "@/assets/logos/idmc-logo.svg";
import amaLogo from "@/assets/logos/ama_logo-1.svg";
import ohchrLogo from "@/assets/logos/ohchr-logo.svg";
import escLogo from "@/assets/logos/energy-safety-canada-logo.png.asset.json";

import escShot from "@/assets/case-screenshots/energy-safety-canada.webp";
import amaShot from "@/assets/case-screenshots/ama.png.asset.json";
import kohlerShot from "@/assets/case-screenshots/kohler.png.asset.json";
import ironmanShot from "@/assets/case-screenshots/ironman.png.asset.json";
import uelShot from "@/assets/case-screenshots/uel.png.asset.json";
import ohchrShot from "@/assets/case-screenshots/ohchr.png.asset.json";
import idmcShot from "@/assets/case-screenshots/idmc.png.asset.json";
import type { CaseLens, CasePlatform, CaseIndustry } from "@/data/caseStudies";

export type NamedCase = {
  slug: string;
  sanitySlug?: string;
  client: string;
  headline: string;
  outcome: { primary: string; secondary?: string };
  fallbackSituation: string;
  lenses: CaseLens[];
  platforms: CasePlatform[];
  industries: CaseIndustry[];
  brand: {
    logo: string;
    color: string;
    ink: "paper" | "ink";
    logoTreatment?: "silhouette" | "native";
    subtitle?: string;
    partner?: { name: string; logo?: string };
  };

  siteScreenshot?: { url: string; siteUrl: string };
};

export const NAMED_CASES: NamedCase[] = [
  {
    slug: "energy-safety-canada-member-experience",
    client: "Energy Safety Canada",
    headline: "Drupal 11 over iMIS, with the back office untouched.",
    outcome: { primary: "15s → 2s page loads", secondary: "36K organic visits a month at 8.7% CTR" },
    fallbackSituation:
      "A national certification business running on Kentico CMS with an aging iMIS front end: developer-gated content, broken search across 1,000+ resources, and membership, learning, and commerce in separate silos.",
    lenses: ["strategy", "engineering", "design", "marketing"],
    platforms: ["drupal"],
    industries: ["non-profit"],
    brand: { logo: escLogo.url, color: "#0B3D67", ink: "paper" },
    siteScreenshot: { url: escShot, siteUrl: "energysafetycanada.com" },
  },
  {

    slug: "transformation-ama-digital-platform",
    client: "American Medical Association",
    headline: "Ten properties consolidated onto one governed Drupal platform.",
    outcome: { primary: "10→1 properties unified", secondary: "Editorial throughput up materially" },
    fallbackSituation:
      "Ten WordPress and Drupal properties, fragmented governance, and a content team stretched across every release.",
    lenses: ["engineering", "design"],
    platforms: ["drupal"],
    industries: ["non-profit"],
    brand: { logo: amaLogo, color: "#5E2D87", ink: "paper" },
    siteScreenshot: { url: amaShot.url, siteUrl: "ama-assn.org" },
  },
  {
    slug: "transforming-digital-experience-of-kohler",
    client: "KOHLER",
    headline: "Composable headless across a global brand estate.",
    outcome: { primary: "40% faster load times", secondary: "One core, regional brand sites" },
    fallbackSituation:
      "A global brand estate on fragmented stacks, regional teams blocked on shared platform changes.",
    lenses: ["engineering", "design"],
    platforms: ["drupal", "aws"],
    industries: ["retail"],
    brand: { logo: kohlerLogo, color: "#161616", ink: "paper" },
    siteScreenshot: { url: kohlerShot.url, siteUrl: "kohler.com" },
  },
  {
    slug: "transforming-digital-experience-of-ironman",
    client: "IRONMAN",
    headline: "A race-day platform that holds up under global load.",
    outcome: { primary: "99.99% race-day uptime", secondary: "Multi-brand events on one DXP" },
    fallbackSituation:
      "Global event series with race-day traffic spikes, multi-brand registration, and a fragile content pipeline.",
    lenses: ["engineering", "marketing"],
    platforms: ["drupal", "hubspot"],
    industries: ["retail"],
    brand: { logo: ironmanLogo, color: "#D71920", ink: "paper" },
    siteScreenshot: { url: ironmanShot.url, siteUrl: "ironman.com" },
  },
  {
    slug: "dxp-for-university-of-east-london",
    client: "University of East London",
    headline: "A unified digital front door for UEL.",
    outcome: { primary: "2.3× applicant conversion", secondary: "Design system the in-house team owns" },
    fallbackSituation:
      "Higher-ed estate with fractured journeys, slow publishing, and applicant funnels leaking at every step.",
    lenses: ["design", "engineering", "marketing"],
    platforms: ["drupal", "acquia"],
    industries: ["higher-ed"],
    brand: { logo: uelLogo, color: "#4DBCB0", ink: "paper" },
    siteScreenshot: { url: uelShot.url, siteUrl: "uel.ac.uk" },
  },
  {
    slug: "transforming-digital-experience-content-publishing-for-ohchr",
    client: "OHCHR",
    headline: "Reimagined DX and content publishing on Drupal 9.",
    outcome: { primary: "Security and content integrity hardened", secondary: "Editorial UX rebuilt" },
    fallbackSituation:
      "United Nations human-rights office publishing in a high-stakes environment, with security and content integrity non-negotiable.",
    lenses: ["design", "engineering"],
    platforms: ["drupal"],
    industries: ["non-profit"],
    brand: { logo: ohchrLogo, color: "#009EDB", ink: "paper" },
    siteScreenshot: { url: ohchrShot.url, siteUrl: "ohchr.org" },
  },
  {
    slug: "elevating-idmc-user-experience",
    sanitySlug: "elevating-idmcs-user-experience",
    client: "Internal Displacement Monitoring Centre",
    headline: "Monolith to composable DXP, without losing the content team.",
    outcome: { primary: "SEO performance up 49%", secondary: "Site performance up 38%" },
    fallbackSituation:
      "Monolithic CMS slowing publishing and SEO, with a small content team unable to take on a heavy migration.",
    lenses: ["engineering", "marketing"],
    platforms: ["drupal"],
    industries: ["non-profit"],
    brand: { logo: idmcLogo, color: "#018ECA", ink: "paper" },
    siteScreenshot: { url: idmcShot.url, siteUrl: "internal-displacement.org" },
  },
];

export const NAMED_SLUGS = NAMED_CASES.map((c) => c.slug);

export function findNamedCase(slug: string): NamedCase | undefined {
  return NAMED_CASES.find((c) => c.slug === slug);
}
